import * as React from 'react';
import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { NavLink } from 'react-router-dom';

const SubmenuWrapper = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-submenu-wrapper',
      'flex flex-col gap-4',
      'text-primary-dark',

      // Mobile version styles
      'max-md:hidden',
      'max-md:px-4 max-md:pb-4 max-md:pt-0',
      'max-md:[.is-expanded_&]:flex max-md:[.is-expanded_&]:flex-col', // isExpanded styles

      // Desktop version styles
      'md:bg-white',
      'md:shadow-[8px_4px_10px_0px_rgba(119,119,120,0.15)]',
      'md:w-[188px]',
      'md:h-screen',
      'md:px-4 md:py-8',
      'md:fixed md:left-16 md:top-0',
      'md:overflow-y-auto',

      // Transition styles and initial hidden state
      'md:pointer-events-none md:opacity-0',
      'md:transition-opacity md:duration-100',
      // Show submenu on parent hover
      'md:group-hover/has-submenu:pointer-events-auto md:group-hover/has-submenu:opacity-100',

      className
    )}
    {...props}
  >
    {children}
  </div>
));
SubmenuWrapper.displayName = 'SubmenuWrapper';

const SubmenuHeader = React.forwardRef(({ className, data, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-submenu-header',
      'flex flex-col items-center text-center',
      'bg-neutral-bright',
      'p-5 px-3',
      'rounded-lg',
      // Mobile version styles
      'max-md:hidden',
      className
    )}
    {...props}
  />
));
SubmenuHeader.displayName = 'SubmenuHeader';

const SubmenuImage = React.forwardRef(({ className, ...props }, ref) => (
  <img
    ref={ref}
    className={cn('c-submenu-image', 'h-[68px] w-[80px]', 'mb-3', className)}
    {...props}
  />
));
SubmenuImage.displayName = 'SubmenuImage';

const SubmenuTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('c-submenu-title', 'text-primary-dark', 'text-base leading-tight', 'font-bold', 'mb-0', className)}
    {...props}
  />
));
SubmenuTitle.displayName = 'SubmenuTitle';

const SubmenuSubtitle = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('c-submenu-subtitle', 'text-neutral-dark', 'text-xs', 'font-light', 'leading-tight', 'mb-0', className)}
    {...props}
  />
));
SubmenuSubtitle.displayName = 'SubmenuSubtitle';

const SubmenuBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-submenu-body', className)}
    {...props}
  />
));
SubmenuBody.displayName = 'SubmenuBody';

const SubmenuNav = React.forwardRef(({ className, ...props }, ref) => (
  <nav
    ref={ref}
    className={cn('c-submenu-nav', className)}
    {...props}
  />
));
SubmenuNav.displayName = 'SubmenuNav';

const SubmenuList = React.forwardRef(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn(
      'c-submenu-list',
      'm-0 list-none p-0',
      'flex flex-col',
      // Mobile version styles
      'max-md:gap-0',
      // Desktop version styles
      'md:gap-5',
      className
    )}
    {...props}
  />
));
SubmenuList.displayName = 'SubmenuList';

const SubmenuItem = React.forwardRef(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn('c-submenu-item', className)}
    {...props}
  />
));
SubmenuItem.displayName = 'SubmenuItem';

const SubmenuLink = React.forwardRef(({ className, asChild, ...props }, ref) => {
  const Comp = asChild ? Slot : NavLink;

  return (
    <Comp
      ref={ref}
      className={cn(
        'c-submenu-link',
        'flex items-start',
        'p-2',
        'gap-2',
        'rounded-lg',
        'bg-white',
        'transition-colors duration-150',
        'cursor-pointer',
        // Hover/Focus
        'hover:bg-bright',
        'focus:bg-bright',
        // isActive styles
        'aria-[current=page]:bg-neutral-lightest',
        'active:bg-neutral-lightest',
        className
      )}
      {...props}
    />
  );
});
SubmenuLink.displayName = 'SubmenuLink';

const SubmenuLinkContent = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('c-submenu-link-content', className)}
    {...props}
  />
));
SubmenuLinkContent.displayName = 'SubmenuLinkContent';

const SubmenuLinkTitle = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('c-submenu-link-title', 'block', 'text-primary', 'text-xs', 'font-bold', 'leading-normal', className)}
    {...props}
  />
));
SubmenuLinkTitle.displayName = 'SubmenuLinkTitle';

const SubmenuLinkDescription = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('c-submenu-link-description', 'block', 'text-neutral', 'text-xs', 'font-light', 'leading-normal', className)}
    {...props}
  />
));
SubmenuLinkDescription.displayName = 'SubmenuLinkDescription';

const SubmenuIcon = React.forwardRef(({ className, ...props }, ref) => (
  <Slot
    ref={ref}
    className={cn('c-submenu-icon', 'w-4', 'flex-shrink-0', 'text-primary', className)}
    {...props}
  />
));
SubmenuIcon.displayName = 'SubmenuIcon';

const Submenu = React.forwardRef(({ className, data, ...props }, ref) => {
  return (
    <SubmenuWrapper
      className={cn('c-submenu', className)}
      ref={ref}
      {...props}
    >
      <SubmenuHeader>
        <SubmenuImage
          src={data?.img}
          alt=""
        />
        <SubmenuTitle>{data?.title}</SubmenuTitle>
        <SubmenuSubtitle>{data?.subtitle}</SubmenuSubtitle>
      </SubmenuHeader>

      <SubmenuBody>
        <SubmenuNav>
          <SubmenuList>
            {data?.nav
              ?.filter(item => item?.visible)
              .map((item, key) => (
                <SubmenuItem key={key}>
                  <SubmenuLink to={item?.route}>
                    <SubmenuIcon>{item?.icon}</SubmenuIcon>
                    <SubmenuLinkContent>
                      <SubmenuLinkTitle>{item?.title}</SubmenuLinkTitle>
                      <SubmenuLinkDescription>{item?.description}</SubmenuLinkDescription>
                    </SubmenuLinkContent>
                  </SubmenuLink>
                </SubmenuItem>
              ))}
          </SubmenuList>
        </SubmenuNav>
      </SubmenuBody>
    </SubmenuWrapper>
  );
});
Submenu.displayName = 'Submenu';

export { Submenu, SubmenuWrapper, SubmenuHeader, SubmenuImage, SubmenuTitle, SubmenuSubtitle, SubmenuBody, SubmenuNav, SubmenuList, SubmenuItem, SubmenuLink, SubmenuLinkContent, SubmenuLinkTitle, SubmenuLinkDescription, SubmenuIcon };
